import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../../components/ui/table';
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Shield,
  Clock,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminSubscriptionApi } from './adminApi';

const ACTOR_TYPES = ['admin', 'system', 'webhook', 'user'];
const ACTIONS = [
  'force_status', 'extend_subscription', 'grant_plan', 'refund',
  'create_plan', 'update_plan', 'deactivate_plan', 'retry_webhook',
  'subscription_created', 'subscription_updated', 'subscription_canceled',
  'payment_succeeded', 'payment_failed',
];
const RESOURCE_TYPES = ['subscription', 'transaction', 'plan', 'webhook', 'user'];

const ACTION_COLORS = {
  force_status: 'bg-orange-100 text-orange-800 border-orange-200',
  extend_subscription: 'bg-blue-100 text-blue-800 border-blue-200',
  grant_plan: 'bg-purple-100 text-purple-800 border-purple-200',
  refund: 'bg-red-100 text-red-800 border-red-200',
  create_plan: 'bg-green-100 text-green-800 border-green-200',
  update_plan: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  deactivate_plan: 'bg-red-100 text-red-800 border-red-200',
  retry_webhook: 'bg-gray-100 text-gray-800 border-gray-200',
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

function DiffView({ before, after }) {
  if (!before && !after) return <p className="text-sm text-muted-foreground">No change data available</p>;

  const beforeObj = typeof before === 'string' ? (() => { try { return JSON.parse(before); } catch { return null; } })() : before;
  const afterObj = typeof after === 'string' ? (() => { try { return JSON.parse(after); } catch { return null; } })() : after;

  if (!beforeObj && !afterObj) {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Before</p>
          <pre className="p-2 bg-red-50 border border-red-100 rounded text-xs overflow-auto max-h-40">{before || 'N/A'}</pre>
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">After</p>
          <pre className="p-2 bg-green-50 border border-green-100 rounded text-xs overflow-auto max-h-40">{after || 'N/A'}</pre>
        </div>
      </div>
    );
  }

  const allKeys = new Set([
    ...Object.keys(beforeObj || {}),
    ...Object.keys(afterObj || {}),
  ]);

  const changedKeys = [...allKeys].filter((k) => {
    const bVal = JSON.stringify(beforeObj?.[k]);
    const aVal = JSON.stringify(afterObj?.[k]);
    return bVal !== aVal;
  });

  if (changedKeys.length === 0) {
    return <p className="text-sm text-muted-foreground">No changes detected</p>;
  }

  return (
    <div className="space-y-2">
      {changedKeys.map((key) => (
        <div key={key} className="flex items-start gap-3 text-xs">
          <span className="font-mono font-medium min-w-[120px] text-muted-foreground">{key}</span>
          <span className="px-1.5 py-0.5 bg-red-50 text-red-700 rounded line-through">
            {JSON.stringify(beforeObj?.[key]) ?? 'null'}
          </span>
          <span className="text-muted-foreground">-&gt;</span>
          <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded">
            {JSON.stringify(afterObj?.[key]) ?? 'null'}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function AdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [filters, setFilters] = useState({ actor_type: '', action: '', resource_type: '', date_from: '', date_to: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });

      const res = await adminSubscriptionApi.listAuditLogs(params);
      setLogs(res.data || res.audit_logs || []);
      setTotal(res.total || 0);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setPage(1); }, [filters]);

  const totalPages = Math.ceil(total / perPage) || 1;

  const clearFilters = () => {
    setFilters({ actor_type: '', action: '', resource_type: '', date_from: '', date_to: '' });
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Shield className="h-6 w-6" />
          Audit Log
        </h1>
        <p className="text-sm text-muted-foreground">
          Immutable record of all administrative actions. {total.toLocaleString()} entries.
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${showFilters ? 'bg-primary text-primary-foreground' : 'border-input bg-background hover:bg-accent'}`}
            >
              <Filter className="h-4 w-4" />
              Filters
            </button>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="inline-flex items-center gap-1 px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-4 w-4" /> Clear
              </button>
            )}
          </div>

          {showFilters && (
            <div className="flex flex-wrap items-end gap-3 pt-2 border-t">
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Actor Type</label>
                <select
                  value={filters.actor_type}
                  onChange={(e) => setFilters((f) => ({ ...f, actor_type: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                >
                  <option value="">All</option>
                  {ACTOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Action</label>
                <select
                  value={filters.action}
                  onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                >
                  <option value="">All</option>
                  {ACTIONS.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Resource Type</label>
                <select
                  value={filters.resource_type}
                  onChange={(e) => setFilters((f) => ({ ...f, resource_type: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                >
                  <option value="">All</option>
                  {RESOURCE_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Date From</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Date To</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Timestamp</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Summary</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}><div className="h-4 bg-muted rounded animate-pulse w-16" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No audit log entries found</TableCell>
                </TableRow>
              ) : (
                logs.map((log) => {
                  const isExpanded = expandedRow === log.id;
                  return (
                    <React.Fragment key={log.id}>
                      <TableRow
                        className="cursor-pointer"
                        onClick={() => setExpandedRow(isExpanded ? null : log.id)}
                      >
                        <TableCell>
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(log.created_at || log.timestamp)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">{log.actor || log.admin_email || '-'}</p>
                            {log.actor_type && (
                              <Badge variant="outline" className="text-xs mt-0.5">{log.actor_type}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                            {log.action?.replace(/_/g, ' ')}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {log.resource_type && <Badge variant="secondary" className="text-xs">{log.resource_type}</Badge>}
                            {log.resource_id && <p className="font-mono text-xs text-muted-foreground mt-0.5">{log.resource_id}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate text-muted-foreground">
                          {log.changes_summary || log.reason || '-'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">
                          {log.ip_address ? (
                            <span className="flex items-center gap-1">
                              <Globe className="h-3 w-3" />
                              {log.ip_address}
                            </span>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-muted/30 p-0">
                            <div className="p-4 space-y-4">
                              {log.reason && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-1">Reason</p>
                                  <p className="text-sm">{log.reason}</p>
                                </div>
                              )}
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-2">Changes</p>
                                <DiffView before={log.before || log.changes?.before} after={log.after || log.changes?.after} />
                              </div>
                              {log.metadata && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-1">Metadata</p>
                                  <pre className="p-3 bg-background border rounded-md text-xs overflow-auto max-h-40 font-mono">
                                    {JSON.stringify(log.metadata, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {Math.min(((page - 1) * perPage) + 1, total)}-{Math.min(page * perPage, total)} of {total}
        </p>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-md border border-input hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-3 py-1 text-sm font-medium">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-md border border-input hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
